import { useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTh } from '@/components/table'
import { Plus, Pencil, Trash2, Loader2, AlertCircle } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { sourcesApi, type Source } from '@/api/sourcesApi'
import { SourceFormModal } from './SourceFormModal'

/** Pull the clean server message out of the API client's wrapped error string. */
function readableError(e: unknown): string {
  const raw = e instanceof Error ? e.message : 'Something went wrong.'
  const jsonStart = raw.indexOf('{')
  if (jsonStart !== -1) {
    try {
      const body = JSON.parse(raw.slice(jsonStart)) as { message?: string | string[] }
      if (body.message) return Array.isArray(body.message) ? body.message.join(', ') : body.message
    } catch {
      /* fall through to the raw string */
    }
  }
  return raw
}

/**
 * Inline sources manager (Add / Edit / Remove) rendered inside the Integrations
 * page's "Manage Sources" tab. Sources are the catalog of system/device types
 * integrations are built from.
 */
// Per-user column layout. Name is locked — it identifies the source. See
// src/components/table/index.ts for the pattern.
const SOURCES_TABLE_ID = 'integration-sources'

const SOURCES_COLUMNS: TableColumnDef[] = [
  { id: 'name', header: 'Name', locked: true, sortable: true },
  { id: 'key', header: 'Key', sortable: true },
  { id: 'category', header: 'Category', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

// Column sorting (z8nrz7e4h6). Category sorts as its badge reads.
const SOURCES_SORT: SortAccessors<Source> = {
  name: (s) => s.name,
  key: (s) => s.key,
  category: (s) => s.category?.replace('_', ' ') ?? 'other',
}

export function ManageSourcesPanel() {
  const cols = useTableColumns(SOURCES_TABLE_ID, SOURCES_COLUMNS)
  const queryClient = useQueryClient()
  const { data: sources = [], isLoading } = useQuery<Source[]>({
    queryKey: ['sources'],
    queryFn: sourcesApi.list,
  })

  const sortedSources = useSortedRows(sources, cols.sort, SOURCES_SORT)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Source | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Source | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  function openAdd() {
    setEditing(null)
    setFormOpen(true)
  }
  function openEdit(source: Source) {
    setEditing(source)
    setFormOpen(true)
  }

  async function handleDelete(source: Source) {
    setDeleting(true)
    setDeleteError(null)
    try {
      await sourcesApi.remove(source.id)
      await queryClient.invalidateQueries({ queryKey: ['sources'] })
      setDeleteTarget(null)
    } catch (e) {
      setDeleteError(readableError(e))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Sources are the catalog of system/device types integrations are built from.
        </p>
        <Button size="sm" onClick={openAdd}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Source
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : sources.length === 0 ? (
        <div className="text-center py-12 text-sm text-muted-foreground">
          No sources yet. Add one to get started.
        </div>
      ) : (
        <>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-table-header border-b">
              <tr>
                {cols.visible.map(({ def }) => (
                  <ResizableTh
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={
                      def.id === 'actions'
                        ? 'px-4 py-2.5'
                        : 'text-left px-4 py-2.5 font-medium text-table-header-foreground'
                    }
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedSources.map((s, i) => (
                <tr key={s.id} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                  <ColumnCells
                    columns={cols}
                    cells={{
                      name: <td className="px-4 py-2.5 font-medium">{s.name}</td>,
                      key: <td className="px-4 py-2.5 text-muted-foreground font-mono text-xs">{s.key}</td>,
                      category: (
                        <td className="px-4 py-2.5">
                          <Badge className="bg-gray-100 text-gray-700 border-gray-200 text-xs border capitalize">
                            {s.category?.replace('_', ' ') ?? 'other'}
                          </Badge>
                        </td>
                      ),
                      actions: (
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEdit(s)}>
                              <Pencil className="h-3.5 w-3.5 mr-1.5" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                setDeleteError(null)
                                setDeleteTarget(s)
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      ),
                    }}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Add / edit form */}
      <SourceFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        source={editing}
        onSaved={() => setFormOpen(false)}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (deleting) return
          if (!o) {
            setDeleteTarget(null)
            setDeleteError(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove Source</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{deleteTarget?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <div className="flex items-start gap-2 text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              {deleteError}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteTarget(null)
                setDeleteError(null)
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && handleDelete(deleteTarget)}
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
